import { ListFilter } from 'lucide-react';
import { type MouseEvent, type ReactElement, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import styled from 'styled-components';

import { CenterFlex, Flex } from '@/components/base';
import { RoundedButton } from '@/components/Button/RoundedButton/FilterButton';
import Menu from '@/components/Menu/Menu';
import useModalState from '@/hooks/pure/useModalState';
import {
  DateRangePicker,
  type DateRangePickerValue,
} from '@/shared/ui/date-range-picker';
import {
  FEEDBACK_COLUMNS,
  type FullColumnKey,
  SENTIMENT_COLUMNS,
  SOURCE_COLUMNS,
} from '@/types/filterKeyConst';
import type { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';
import { getDropdownPosWithTrigger } from '@/utils/dom';

import FieldItemForInsight from './components/FieldItemForInsight';
import FieldItemSelect from './components/FieldItemSelect';
import { GhostFieldItem } from './components/GhostFieldItem';
import useStructuredFilterMap from './configs/useStructuredFilterMap';
import { useStructuredFilterMenus } from './hooks/useStructuredFilterMenus';
import structuredFilterUtils from './utils/structuredFilter.utils';

type Props = {
  values: StructuredFilterQueryFormatT[];
  onChange: (values: StructuredFilterQueryFormatT[]) => void;
  triggerNode?: ReactElement;
  clearFilter: () => void;
  disAllowFilter?: FullColumnKey[];
  dateRange: DateRangePickerValue;
  setDateRange: (dateRange: DateRangePickerValue) => void;
  quickFilterKeys?: FullColumnKey[];
};

const DEFAULT_QUICK_FILTER_KEYS: FullColumnKey[] = [
  SOURCE_COLUMNS.SOURCE_TYPE,
  SENTIMENT_COLUMNS.SENTIMENT_DETAIL,
  FEEDBACK_COLUMNS.TAG,
];

const StructuredFilterForInsight = (props: Props) => {
  const fieldOptions = useStructuredFilterMap();
  const ref = useRef<HTMLDivElement>(null);
  const [subMenuStatus, setSubMenuStatus] = useState<string | null>(null);

  const {
    open: openFieldItemSelect,
    close: closeFieldItemSelect,
    status: fieldItemSelectStatus,
    modalState: fieldItemSelectState,
    pos: fieldItemSelectPosition,
  } = useModalState<
    StructuredField & {
      valueIndex: number;
    }
  >();

  const {
    open: openMenu,
    close: closeMenu,
    status: menuStatus,
  } = useModalState();

  const menuOptions = useStructuredFilterMenus({
    triggerRef: ref,
    openFieldItemSelect,
    values: props.values,
    disAllowFilter: props.disAllowFilter,
    closeMenu,
    setSubMenuStatus,
    subMenuStatus,
  });

  const onOperatorChange = (
    operator: STRUCTURED_FILTER_OPERATOR,
    index: number,
  ) => {
    const newValues = [...props.values];
    if (newValues[index]) {
      newValues[index] = {
        ...newValues[index],
        operator,
      };
      props.onChange(newValues);
    }
  };

  const onClickValue = (
    e: MouseEvent<HTMLElement>,
    index: number,
    fieldInfo: StructuredField,
  ) => {
    const pos = getDropdownPosWithTrigger(e.currentTarget);
    openFieldItemSelect({
      pos: pos.pos,
      modalState: {
        valueIndex: index,
        ...fieldInfo,
      },
    });
  };

  const onGhostFieldItemClick = (
    e: MouseEvent<HTMLElement>,
    fieldOption: StructuredField,
  ) => {
    const pos = getDropdownPosWithTrigger(e.currentTarget);
    openFieldItemSelect({
      pos: pos.pos,
      modalState: {
        ...fieldOption,
        valueIndex: props.values.length,
      },
    });
  };

  return (
    <ErrorBoundary
      fallbackRender={() => {
        return <div>Error</div>;
      }}
    >
      <Style>
        <Menu
          anchorRef={ref}
          modalWidth="200px"
          isOpen={menuStatus}
          openMenu={openMenu}
          closeMenu={closeMenu}
          triggerNode={
            props.triggerNode || (
              <RoundedButton>
                <ListFilter className="size-3 -ml-0.5" />
                Filter
              </RoundedButton>
            )
          }
          options={menuOptions}
        />
        <DateRangePicker
          value={props.dateRange}
          onChange={(value) => props.setDateRange(value)}
        />

        {props.values.map((filterQuery, index) => {
          return (
            <ErrorBoundary
              key={index}
              fallbackRender={() => {
                return <CenterFlex padding="8px">Field item error</CenterFlex>;
              }}
            >
              {(function () {
                const result = structuredFilterUtils.getFieldInfo(
                  filterQuery.columnKey,
                  fieldOptions,
                );
                if (!result) return null;
                const { fieldInfo, parentFieldName } = result;
                return (
                  <FieldItemForInsight
                    filterIndex={index}
                    parentFieldName={parentFieldName}
                    columnName={fieldInfo.columnName}
                    isMultiValueField={fieldInfo.isMultiValueField}
                    filterQuery={filterQuery}
                    onClickValue={(e) => onClickValue(e, index, fieldInfo)}
                    onOperatorChange={(operator) =>
                      onOperatorChange(operator, index)
                    }
                    onRemoveClick={() => {
                      props.onChange(
                        props.values.filter((_, i) => i !== index),
                      );
                    }}
                  />
                );
              })()}
            </ErrorBoundary>
          );
        })}
        {(props.quickFilterKeys ?? DEFAULT_QUICK_FILTER_KEYS)
          .filter(
            (columnKey) =>
              !props.values.some(
                (filterQuery) => filterQuery.columnKey === columnKey,
              ),
          )
          .map((columnKey) => {
            const findFieldOption = (columnKey: FullColumnKey) => {
              const fieldOption = fieldOptions[columnKey];
              if (fieldOption) return fieldOption;
              const fieldOptionsArray = Object.values(fieldOptions);
              for (const field of fieldOptionsArray) {
                if (field.childField) {
                  const childField = field.childField[columnKey];
                  if (childField) return childField;
                }
              }
              return null;
            };
            const fieldOption = findFieldOption(columnKey);
            if (!fieldOption) return null;
            return (
              <GhostFieldItem
                fieldName={fieldOption?.columnName}
                key={columnKey}
                onClick={(e) => {
                  onGhostFieldItemClick(e, fieldOption);
                }}
              />
            );
          })}

        {fieldItemSelectState && fieldItemSelectStatus && (
          <FieldItemSelect
            initailValues={props.values[fieldItemSelectState.valueIndex]}
            structuredField={fieldItemSelectState}
            status={fieldItemSelectStatus}
            close={closeFieldItemSelect}
            pos={fieldItemSelectPosition}
            onChange={(filterQueryFormat) => {
              let newValues = [...props.values];
              newValues[fieldItemSelectState.valueIndex] = filterQueryFormat;

              if (fieldItemSelectState.filterItemOptions?.type === 'range') {
                if (
                  !filterQueryFormat.rangeValues?.min &&
                  !filterQueryFormat.rangeValues?.max
                ) {
                  newValues = newValues.filter(
                    (_, i) => i !== fieldItemSelectState.valueIndex,
                  );
                }
              } else {
                if (filterQueryFormat.filterValues.length === 0) {
                  newValues = newValues.filter(
                    (_, i) => i !== fieldItemSelectState.valueIndex,
                  );
                }
              }
              props.onChange(newValues);
            }}
          />
        )}
      </Style>
    </ErrorBoundary>
  );
};

const Style = styled(Flex)`
  flex: 1;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
`;

export default StructuredFilterForInsight;
